import { useQuery } from '@tanstack/react-query'
import { fetchTickStatus, tickKeys, type TickStatusData } from '@/services/ticks'

/**
 * Hook to get comprehensive tick status for a world.
 *
 * Returns the current tick, last tick status, and optionally election countdown.
 *
 * @param worldId - The world to check (hook disabled if undefined)
 * @param nationId - Optional nation to get election countdown
 * @returns Tick status data with loading/error states
 */
export function useTickStatus(worldId: string | undefined, nationId?: string) {
  const query = useQuery({
    queryKey: tickKeys.status(worldId!, nationId),
    queryFn: () => fetchTickStatus(worldId!, nationId),
    enabled: !!worldId,
    // Refetch every 30 seconds to keep status fresh
    refetchInterval: 30000,
    // Stale after 10 seconds to allow manual refetches
    staleTime: 10000,
  })

  return {
    data: query.data as TickStatusData | undefined,
    currentTick: query.data?.currentTick ?? 0,
    maxTick: query.data?.maxTick ?? null,
    lastTickStatus: query.data?.lastTickStatus ?? 'pending',
    lastTickCompletedAt: query.data?.lastTickCompletedAt ?? null,
    nextElectionTick: query.data?.nextElectionTick ?? null,
    ticksUntilElection: query.data?.ticksUntilElection ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}
